<template>
    <div
        :class="{
            'dk-chips': true,
            'dk-chips--wrap': wrap,
        }"
    >
        <div class="dk-chips__wrapper tw-flex tw-gap-2 tw-py-6 lg:tw-py-8">
            <dk-chip
                v-for="(chip, index) in value"
                v-show="!(index >= maxItems && !showMore)"
                :key="new Date().getTime() + index"
                :item="{ ...chip, index }"
                :clickable="!isStatic"
                :active="chip.active"
                :disabled="chip.disabled"
                :deletable="chip.deletable"
                :variant="variant"
                :color="color"
                class="dk-chips__item"
                @click="onChipClick"
            >
                <span v-html="chip.name"></span>
            </dk-chip>
            <dk-chip
                v-if="shouldShowToggler"
                class="dk-chips__item"
                clickable
                :variant="variant"
                :color="color"
                :has-toggler-style="true"
                @click="toggleShowMore"
            >
                <span>
                    {{ togglerText }}
                </span>
            </dk-chip>
        </div>
    </div>
</template>

<script>
/**
 * Chip list
 *
 * @version 4.0.0
 * @example ./examples/DkChips.md
 */
export default {
    name: 'DkChips',
    components: {
        DkChip: () => ({
            component: import(/* webpackChunkName: "DkChip-vue" */ '@v4/common/components/atoms/DkChip.vue'),
        }),
    },
    props: {
        /**
         * Chips items
         */
        value: {
            type: Array,
            default: () => [],
        },
        /**
         * Creates a not-clickable chips
         */
        isStatic: {
            type: Boolean,
            default: false,
        },
        /**
         * Blocks the chips to show
         */
        maxItems: {
            type: Number,
            default: 9,
        },
        /**
         * UI variants. Check [DkChip](#/Atoms/DkChip) component
         */
        variant: {
            type: String,
            default: '',
        },
        /**
         * Chip items break into multiple lines
         */
        wrap: {
            type: Boolean,
            default: false,
        },
        /**
         * Chips color. Available: `turquoise`
         */
        color: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            showMore: false,
        };
    },
    computed: {
        shouldShowToggler() {
            return this.value.length > this.maxItems;
        },
        moreItemsCount() {
            if (this.value.length - this.maxItems < 0) {
                return 0;
            }

            return this.value.length - this.maxItems;
        },
        togglerText() {
            if (this.showMore) {
                return this.$t('dk-chips.show-less-filters');
            }

            if (this.moreItemsCount) {
                return this.$tc('dk-chips.more', this.moreItemsCount, { n: this.moreItemsCount });
            }

            return this.$t('dk-chips.show-more-filters');
        },
    },
    methods: {
        toggleShowMore() {
            this.showMore = !this.showMore;

            /**
             * Fired when clicked on the show more toggler
             *
             * @event toggle
             * @type {object}
             * @property {boolean} toggled - Toggler state
             */
            this.$emit('toggle', this.showMore);
        },
        onChipClick(payload) {
            /**
             * Fired when clicked on the chip item
             *
             * @event click
             * @type {object}
             * @property {object} $event - Click event
             * @property {object} item - Item object
             * @property {object} DkChip - DkChip vue component object
             */
            this.$emit('click', payload);
        },
    },
};
</script>
